/**
 * Reservations Context - Shared state management for reservation modules
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import axios from 'axios';

const ReservationsContext = createContext();

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_RESERVATIONS: 'SET_RESERVATIONS',
  ADD_RESERVATION: 'ADD_RESERVATION',
  UPDATE_RESERVATION: 'UPDATE_RESERVATION',
  REMOVE_RESERVATION: 'REMOVE_RESERVATION',
  SET_FILTERS: 'SET_FILTERS',
  SET_PAGINATION: 'SET_PAGINATION',
  SET_ORG_TAX_RATE: 'SET_ORG_TAX_RATE',
  SET_INVENTORY_ITEMS: 'SET_INVENTORY_ITEMS',
  SET_SERVICES: 'SET_SERVICES'
};

// Initial state
const initialState = {
  reservations: [],
  loading: false,
  error: null,
  filters: {
    status: 'all',
    dateRange: { startDate: '', endDate: '' },
    searchTerm: '',
    customerFilter: '',
    serviceFilter: ''
  },
  pagination: {
    currentPage: 1,
    itemsPerPage: 50,
    totalItems: 0
  },
  sortConfig: {
    key: null,
    direction: 'asc'
  },
  orgTaxRate: 0,
  inventoryItems: [],
  services: []
};

// Reducer
const reservationsReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ACTIONS.SET_RESERVATIONS:
      return { 
        ...state, 
        reservations: action.payload, 
        loading: false, 
        error: null 
      };
    
    case ACTIONS.ADD_RESERVATION:
      return {
        ...state,
        reservations: [action.payload, ...state.reservations]
      };
    
    case ACTIONS.UPDATE_RESERVATION:
      return {
        ...state,
        reservations: state.reservations.map(reservation =>
          reservation.id === action.payload.id ? action.payload : reservation
        )
      };
    
    case ACTIONS.REMOVE_RESERVATION:
      return {
        ...state,
        reservations: state.reservations.filter(reservation =>
          reservation.id !== action.payload
        )
      };
    
    case ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };
    
    case ACTIONS.SET_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
    
    case ACTIONS.SET_ORG_TAX_RATE:
      return { ...state, orgTaxRate: action.payload };
    
    case ACTIONS.SET_INVENTORY_ITEMS:
      return { ...state, inventoryItems: action.payload };
    
    case ACTIONS.SET_SERVICES:
      return { ...state, services: action.payload };
    
    default:
      return state;
  }
};

// Provider component
export const ReservationsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reservationsReducer, initialState);

  // Action creators
  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  }, []);

  const setReservations = useCallback((reservations) => {
    dispatch({ type: ACTIONS.SET_RESERVATIONS, payload: reservations });
  }, []);

  const addReservation = useCallback((reservation) => {
    dispatch({ type: ACTIONS.ADD_RESERVATION, payload: reservation });
  }, []);

  const updateReservation = useCallback((reservation) => {
    dispatch({ type: ACTIONS.UPDATE_RESERVATION, payload: reservation });
  }, []);

  const removeReservation = useCallback((reservationId) => {
    dispatch({ type: ACTIONS.REMOVE_RESERVATION, payload: reservationId });
  }, []);

  const setFilters = useCallback((filters) => {
    dispatch({ type: ACTIONS.SET_FILTERS, payload: filters });
  }, []);

  const setPagination = useCallback((pagination) => {
    dispatch({ type: ACTIONS.SET_PAGINATION, payload: pagination });
  }, []);

  const setOrgTaxRate = useCallback((taxRate) => {
    dispatch({ type: ACTIONS.SET_ORG_TAX_RATE, payload: taxRate });
  }, []);

  const setInventoryItems = useCallback((items) => {
    dispatch({ type: ACTIONS.SET_INVENTORY_ITEMS, payload: items });
  }, []);

  const setServices = useCallback((services) => {
    dispatch({ type: ACTIONS.SET_SERVICES, payload: services });
  }, []);

  // API functions
  const fetchTaxRate = useCallback(async () => {
    try {
      const response = await axios.get('/api/settings/financial/tax');
      // API returns: { success: true, data: { enabled, rate, inclusive, name, jurisdiction } }
      const taxRate = response.data?.data?.rate || 0;
      console.log('[ReservationsContext] Fetched tax rate:', taxRate, 'Full response:', response.data);

      // Convert to percentage if stored as decimal (e.g., 0.10 -> 10)
      const taxRatePercent = taxRate < 1 ? taxRate * 100 : taxRate;

      setOrgTaxRate(taxRatePercent);
      return taxRatePercent;
    } catch (error) {
      console.error('Failed to fetch tax rate:', error);
      setOrgTaxRate(0);
      return 0;
    }
  }, [setOrgTaxRate]);

  const fetchInventoryItems = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use same approach as legacy - filter by status on server side
      const response = await axios.get('/api/inventory/items', {
        params: {
          status: 'AVAILABLE'
        }
      });
      
      
      // Try both response formats - direct array or nested data
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (Array.isArray(response.data?.data)) {
        items = response.data.data;
      } else {
        console.warn('🔍 [ReservationsContext] Unexpected response format:', response.data);
        items = [];
      }
      
      
      setInventoryItems(items);
      return items;
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError('Failed to load inventory items');
      setInventoryItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setInventoryItems]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/services');
      const services = response.data?.data || [];
      setServices(services);
      return services;
    } catch (error) {
      console.error('Failed to fetch services:', error);
      setError('Failed to load services');
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setServices]);

  // Context value
  const value = {
    // State
    ...state,
    
    // Actions
    setLoading,
    setError,
    setReservations,
    addReservation,
    updateReservation,
    removeReservation,
    setFilters,
    setPagination,
    setOrgTaxRate,
    setInventoryItems,
    setServices,
    
    // API functions
    fetchTaxRate,
    fetchInventoryItems,
    fetchServices
  };

  return (
    <ReservationsContext.Provider value={value}>
      {children}
    </ReservationsContext.Provider>
  );
};

// Custom hook to use the context
export const useReservations = () => {
  const context = useContext(ReservationsContext);
  if (!context) {
    throw new Error('useReservations must be used within a ReservationsProvider');
  }
  return context;
};

export default ReservationsContext;